import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import { Bug, Lightbulb, Image as ImageIcon } from 'lucide-react';

const api = axios.create({ baseURL: '/api/platform', withCredentials: true });
const field = { padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid var(--input-border, var(--border))', background: 'var(--input-bg, var(--surface))', color: 'var(--text-main)', fontSize: '0.85rem' };
const ghost = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text-main)', cursor: 'pointer', fontSize: 13 };
const muted = { color: 'var(--text-muted)', fontSize: 13 };
const STATUS = { NEW: ['New', '#6366f1'], IN_PROGRESS: ['In progress', '#d97706'], RESOLVED: ['Resolved', '#10b981'], CLOSED: ['Closed', '#6b7280'], WONT_FIX: ["Won't fix", '#6b7280'] };
const SEV = { LOW: '#6b7280', MEDIUM: '#d97706', HIGH: '#dc2626', CRITICAL: '#7f1d1d' };

const Pill = ({ text, color }) => <span style={{ fontSize: 11, fontWeight: 700, color, border: `1px solid ${color}`, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' }}>{text}</span>;

// One report, expanded: details, context, screenshot, and the triage form.
const Detail = ({ r, onSaved }) => {
    const [status, setStatus] = useState(r.status);
    const [notes, setNotes] = useState(r.adminNotes || '');
    const [message, setMessage] = useState('');
    const [notify, setNotify] = useState(true);
    const [result, setResult] = useState(null);
    const save = useMutation({
        mutationFn: () => api.patch(`bugs/${r.id}`, { status, adminNotes: notes, ...(status !== r.status ? { message, notify } : {}) }).then(res => res.data),
        // The confirmation is shown above the list: with a status filter the row may disappear.
        onSuccess: (data) => { setMessage(''); onSaved(`${r.ref} saved as ${STATUS[data.status][0].toLowerCase()}${data.notified ? `; ${r.reporterName} was emailed` : ''}.`); },
        onError: (e) => setResult(e.response?.data?.error || 'Save failed'),
    });
    const ctx = r.context || {};
    return (
        <div style={{ padding: '12px 16px 16px', background: 'var(--accent-subtle)', display: 'grid', gap: 12 }}>
            <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-main)' }}>{r.description}</div>
            {r.stepsToReproduce && <div><strong>Steps to reproduce</strong><div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-main)' }}>{r.stepsToReproduce}</div></div>}
            <div style={muted}>
                <strong>Reporter:</strong> {r.reporterName} &lt;<a href={`mailto:${r.reporterEmail}?subject=${encodeURIComponent(`Re: ${r.ref} ${r.title}`)}`}>{r.reporterEmail}</a>&gt; · {r.reporterRole} · <strong>Page:</strong> {r.pagePath || '—'}
                <br />{Object.entries(ctx).filter(([k]) => k !== 'recentErrors').map(([k, v]) => `${k}: ${v}`).join(' · ')}
            </div>
            {ctx.recentErrors?.length > 0 && (
                <div><strong style={{ fontSize: 13 }}>Recent errors in their browser</strong>
                    <ul style={{ margin: '4px 0', fontFamily: 'monospace', fontSize: 12 }}>{ctx.recentErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
            )}
            {r.hasScreenshot && (
                <a href={`/api/platform/bugs/${r.id}/screenshot`} target="_blank" rel="noreferrer" style={{ display: 'inline-block' }}>
                    <img src={`/api/platform/bugs/${r.id}/screenshot`} alt={`Screenshot for ${r.ref}`} style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 6, border: '1px solid var(--border)' }} />
                </a>
            )}
            <form onSubmit={e => { e.preventDefault(); save.mutate(); }} style={{ display: 'grid', gap: 8, maxWidth: 720 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label>Status{' '}
                        <select aria-label="Status" style={field} value={status} onChange={e => setStatus(e.target.value)}>
                            {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </label>
                    {status !== r.status && <label style={muted}><input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} /> Email {r.reporterName}</label>}
                </div>
                {status !== r.status && notify && (
                    <textarea aria-label="Message to reporter" rows={2} style={{ ...field, resize: 'vertical', fontFamily: 'inherit' }} value={message} onChange={e => setMessage(e.target.value)} placeholder="Message to the reporter (optional), e.g. Fixed in today's update." />
                )}
                <textarea aria-label="Internal notes" rows={2} style={{ ...field, resize: 'vertical', fontFamily: 'inherit' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal notes (never sent to the reporter)" />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button type="submit" className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 13 }} disabled={save.isPending}>Save</button>
                    {result && <span style={muted}>{result}</span>}
                </div>
            </form>
        </div>
    );
};

export default function BugReportsPanel({ tenants = [] }) {
    const qc = useQueryClient();
    const [filters, setFilters] = useState({ status: 'NEW', type: '', tenantId: '', search: '' });
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(null);
    const [notice, setNotice] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['platform-bugs', filters, page],
        queryFn: () => {
            const p = new URLSearchParams({ page });
            Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
            return api.get(`bugs?${p}`).then(r => r.data);
        },
        placeholderData: keepPreviousData,
    });
    const items = data?.items || [];
    const counts = data?.counts || {};
    const pages = Math.max(1, Math.ceil((data?.total || 0) / (data?.limit || 50)));
    const set = (patch) => { setFilters({ ...filters, ...patch }); setPage(1); setOpen(null); };

    return (
        <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {[['', 'All'], ...Object.entries(STATUS).map(([k, [l]]) => [k, l])].map(([k, l]) => (
                    <button key={k || 'all'} style={{ ...ghost, borderColor: filters.status === k ? 'var(--primary)' : 'var(--border)', fontWeight: filters.status === k ? 600 : 400 }} onClick={() => set({ status: k })}>
                        {l}{k && counts[k] ? ` (${counts[k]})` : ''}
                    </button>
                ))}
                <span style={{ flex: 1 }} />
                <select aria-label="Type filter" style={field} value={filters.type} onChange={e => set({ type: e.target.value })}>
                    <option value="">Bugs and suggestions</option><option value="BUG">Bugs</option><option value="SUGGESTION">Suggestions</option>
                </select>
                <select aria-label="Tenant filter" style={field} value={filters.tenantId} onChange={e => set({ tenantId: e.target.value })}>
                    <option value="">All tenants</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <input aria-label="Search reports" style={field} placeholder="Search or BUG-12" value={filters.search} onChange={e => set({ search: e.target.value })} />
            </div>

            {notice && <div role="status" style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}>{notice}</div>}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {isLoading && <p style={{ ...muted, padding: 16 }}>Loading…</p>}
                {!isLoading && items.length === 0 && <p style={{ ...muted, padding: 16 }}>No reports here.</p>}
                {items.map(r => {
                    const [label, color] = STATUS[r.status];
                    return (
                        <div key={r.id} data-testid="bug-row" style={{ borderTop: '1px solid var(--border)' }}>
                            <button onClick={() => setOpen(open === r.id ? null : r.id)} aria-expanded={open === r.id}
                                style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%', padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--text-main)', flexWrap: 'wrap' }}>
                                {r.type === 'BUG' ? <Bug size={16} color="#ef4444" /> : <Lightbulb size={16} color="#d97706" />}
                                <strong style={{ minWidth: 64 }}>{r.ref}</strong>
                                <span style={{ flex: '1 1 260px' }}>{r.title}{r.hasScreenshot && <ImageIcon size={13} style={{ marginLeft: 6, verticalAlign: -2 }} color="var(--text-muted)" />}</span>
                                {r.severity && <Pill text={r.severity} color={SEV[r.severity]} />}
                                <span style={muted}>{r.tenant?.name}</span>
                                <span style={muted}>{new Date(r.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                                <Pill text={label} color={color} />
                            </button>
                            {open === r.id && <Detail r={r} onSaved={(msg) => { setNotice(msg); qc.invalidateQueries({ queryKey: ['platform-bugs'] }); }} />}
                        </div>
                    );
                })}
            </div>
            {pages > 1 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                    <button style={ghost} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                    <span style={muted}>Page {page} of {pages}</span>
                    <button style={ghost} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
                </div>
            )}
        </div>
    );
}
